<script setup lang="ts">
const props = defineProps<{
  username: string
  isOpen: boolean
}>()

const emit = defineEmits(['close'])

type MenuList = {
  name: string
  url: string
  icon: string
  subMenus?: {
    name: string
    url: string
  }[]
}[]

const menuList: MenuList = [
  {
    name: 'Alat Berat',
    url: '/stock/alat-berat',
    icon: 'mdi:bulldozer',
    subMenus: [
      {
        name: 'Semua',
        url: '/stock/alat-berat'
      },
      {
        name: 'Jenis',
        url: '/stock/alat-berat/jenis'
      },
      {
        name: 'Merk',
        url: '/stock/alat-berat/merk'
      },
      {
        name: 'Model',
        url: '/stock/alat-berat/model'
      },
      {
        name: 'Lokasi',
        url: '/stock/alat-berat/lokasi'
      },
      {
        name: 'Buy',
        url: '/stock/alat-berat/buy/new'
      },
      {
        name: 'Sold',
        url: '/stock/alat-berat/sold'
      }
    ]
  },
  {
    name: 'Sparepart',
    url: '/stock/sparepart',
    icon: 'mdi:cog-outline',
    subMenus: [
      {
        name: 'Semua',
        url: '/stock/sparepart'
      },
      {
        name: 'Tangerang',
        url: '/stock/sparepart?lokasi=3'
      },
      {
        name: 'Samarinda',
        url: '/stock/sparepart?lokasi=2'
      },
      {
        name: 'Jenis',
        url: '/stock/sparepart/jenis'
      },
      {
        name: 'Buy',
        url: '/stock/sparepart/buy/new'
      }
    ]
  },
  {
    name: 'Rent',
    url: '/rent',
    icon: 'mdi:calendar-clock-outline'
  },
  {
    name: 'Copotan',
    url: '/copotan',
    icon: 'mdi:wrench-outline'
  },
  {
    name: 'History',
    url: '/history',
    icon: 'mdi:history'
  }
]

const router = useRouter()
const route = useRoute()
const userAuthStore = useUserAuthStore()

const ROLE_LABELS: Record<number, string> = {
  1: 'Admin',
  2: 'Admin',
  3: 'Staff Cabang',
  4: 'Operational Cabang'
}

const expandedGroups = ref<string[]>([])

watch(() => route.path, (path) => {
  const match = menuList.find(item =>
    item.subMenus && (path === item.url || path.startsWith(`${item.url}/`))
  )

  if (match && !expandedGroups.value.includes(match.name)) {
    expandedGroups.value = [...expandedGroups.value, match.name]
  }
}, { immediate: true })

const mappedRoleMenuList = computed(() => menuList.filter((item) => {
  if (
    userAuthStore.role &&
    Object.keys(ROLE_PATHS).includes(userAuthStore.role.toString()) &&
    ROLE_PATHS[userAuthStore.role as keyof typeof ROLE_PATHS].test(item.url)
  ) {
    return true
  }

  return false
}))

function isActive (url: string) {
  return route.path === url || route.path.startsWith(`${url}/`)
}

function isSubActive (url: string) {
  const [path, query] = url.split('?')

  if (query) {
    return route.fullPath === url
  }

  return route.path === path
}

function toggleGroup (name: string) {
  expandedGroups.value = expandedGroups.value.includes(name)
    ? expandedGroups.value.filter(value => value !== name)
    : [...expandedGroups.value, name]
}

function handleNavigate () {
  emit('close')
}

function logout () {
  useCookie('auth').value = null
  useCookie('token').value = null

  router.push('/')
}

const { data: { value: databaseName } } = await useDatabaseNameGet()
</script>

<template>
  <aside class="sidebar" :class="{ 'is-open': props.isOpen }">
    <div class="sidebar-brand">
      <div class="brand-mark">
        CP
      </div>
      <div class="brand-text">
        <span class="brand-name">Cipondo Powerindo</span>
        <span class="brand-sub">ERP Alat Berat</span>
      </div>
    </div>

    <nav class="sidebar-nav">
      <template v-for="item in mappedRoleMenuList" :key="item.url">
        <NuxtLink
          v-if="!item.subMenus"
          class="sidebar-link"
          :class="{ 'is-active': isActive(item.url) }"
          :to="item.url"
          @click="handleNavigate"
        >
          <Icon :name="item.icon" size="20px" />
          <span>{{ item.name }}</span>
        </NuxtLink>

        <div v-else class="sidebar-group" :class="{ 'is-expanded': expandedGroups.includes(item.name) }">
          <button
            class="sidebar-link group-toggle"
            :class="{ 'is-active': isActive(item.url) }"
            type="button"
            :aria-label="expandedGroups.includes(item.name) ? `Tutup submenu ${item.name}` : `Buka submenu ${item.name}`"
            @click="toggleGroup(item.name)"
          >
            <Icon :name="item.icon" size="20px" />
            <span>{{ item.name }}</span>
            <Icon class="chevron" name="mdi:chevron-down" size="18px" />
          </button>

          <div class="sidebar-submenu">
            <NuxtLink
              v-for="subItem in item.subMenus"
              :key="subItem.url"
              class="sidebar-sublink"
              :class="{ 'is-active': isSubActive(subItem.url) }"
              :to="subItem.url"
              @click="handleNavigate"
            >
              {{ subItem.name }}
            </NuxtLink>
          </div>
        </div>
      </template>
    </nav>

    <div class="sidebar-footer">
      <div class="user-chip">
        <div class="user-avatar">
          {{ props.username.charAt(0).toUpperCase() }}
        </div>
        <div class="user-info">
          <span class="user-name">{{ props.username }}</span>
          <span class="user-role">{{ ROLE_LABELS[userAuthStore.role] || `Role ${userAuthStore.role}` }}</span>
        </div>
      </div>
      <div class="db-chip" title="Database aktif">
        <Icon name="mdi:database-outline" size="14px" />
        <span>{{ databaseName }}</span>
      </div>
      <button class="logout-button" type="button" @click="logout">
        <Icon name="mdi:logout" size="18px" />
        <span>Logout</span>
      </button>
    </div>
  </aside>
</template>

<style lang="scss" scoped>
.sidebar {
  background-color: var(--color-sidebar-bg);
  display: flex;
  flex-direction: column;
  inset-block: 0;
  inset-inline-start: 0;
  position: fixed;
  width: var(--sidebar-width);
  z-index: 50;

  @media screen and (max-width: 1024px) {
    inset-block-end: 0;
    transform: translateX(-100%);
    transition: transform 0.25s ease;

    &.is-open {
      box-shadow: 0 0 40px rgb(0 0 0 / 40%);
      transform: translateX(0);
    }
  }
}

.sidebar-brand {
  align-items: center;
  border-bottom: 1px solid rgb(255 255 255 / 8%);
  display: flex;
  gap: 0.75rem;
  padding: 1.25rem;
}

.brand-mark {
  align-items: center;
  background: linear-gradient(135deg, #4f8cff, #2563eb);
  border-radius: 10px;
  color: #fff;
  display: flex;
  flex-shrink: 0;
  font-weight: 800;
  height: 40px;
  justify-content: center;
  letter-spacing: 0.02em;
  width: 40px;
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;

  .brand-name {
    color: #fff;
    font-size: 0.95rem;
    font-weight: 700;
  }

  .brand-sub {
    color: var(--color-sidebar-text);
    font-size: 0.75rem;
  }
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
}

.sidebar-link {
  align-items: center;
  background-color: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--color-sidebar-text);
  cursor: pointer;
  display: flex;
  font-size: 0.9rem;
  font-weight: 500;
  gap: 0.7rem;
  margin-bottom: 0.2rem;
  padding: 0.65rem 0.75rem;
  text-align: left;
  text-decoration: none;
  transition: background-color 0.15s ease, color 0.15s ease;
  width: 100%;

  &:hover {
    background-color: var(--color-sidebar-bg-hover);
    color: var(--color-sidebar-text-active);
  }

  &.is-active {
    background-color: rgb(79 140 255 / 16%);
    color: var(--color-sidebar-text-active);
  }

  .chevron {
    margin-left: auto;
    transition: transform 0.2s ease;
  }
}

.sidebar-group {
  &.is-expanded {
    .chevron {
      transform: rotate(180deg);
    }

    .sidebar-submenu {
      display: flex;
    }
  }
}

.sidebar-submenu {
  border-left: 1px solid rgb(255 255 255 / 10%);
  display: none;
  flex-direction: column;
  margin: 0.15rem 0 0.4rem 1.9rem;
}

.sidebar-sublink {
  border-radius: 6px;
  color: var(--color-sidebar-text);
  font-size: 0.85rem;
  padding: 0.45rem 0.75rem;
  text-decoration: none;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: var(--color-sidebar-bg-hover);
    color: var(--color-sidebar-text-active);
  }

  &.is-active {
    color: var(--color-sidebar-accent);
    font-weight: 600;
  }
}

.sidebar-footer {
  border-top: 1px solid rgb(255 255 255 / 8%);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 1rem 1rem 1.1rem;
}

.user-chip {
  align-items: center;
  display: flex;
  gap: 0.65rem;
}

.user-avatar {
  align-items: center;
  background-color: rgb(79 140 255 / 25%);
  border-radius: 50%;
  color: #bcd3ff;
  display: flex;
  flex-shrink: 0;
  font-weight: 700;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.user-info {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  min-width: 0;

  .user-name {
    color: #fff;
    font-size: 0.85rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .user-role {
    color: var(--color-sidebar-text);
    font-size: 0.72rem;
  }
}

.db-chip {
  align-items: center;
  background-color: rgb(255 255 255 / 6%);
  border-radius: 6px;
  color: var(--color-sidebar-text);
  display: flex;
  font-size: 0.75rem;
  gap: 0.4rem;
  padding: 0.3rem 0.6rem;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.logout-button {
  align-items: center;
  background-color: transparent;
  border: 1px solid rgb(255 255 255 / 12%);
  border-radius: 8px;
  color: var(--color-sidebar-text);
  cursor: pointer;
  display: flex;
  font-size: 0.85rem;
  font-weight: 600;
  gap: 0.5rem;
  justify-content: center;
  padding: 0.55rem 0.75rem;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background-color: rgb(248 113 113 / 12%);
    border-color: rgb(248 113 113 / 50%);
    color: #fca5a5;
  }
}
</style>
